import { executeStepFunction } from "../../utils/command/stepDataLoader";

export class CommandProcessor {
  constructor(commands, translations, store = null) {
    this.commands = commands;
    this.translations = translations;
    this.store = store; // Store reference for accessing callback registry
  }

  async processSteplessCommand(
    command,
    commandName,
    initialData = {},
    callbacks = {}
  ) {
    let output = "";

    // Add intro if present
    if (command.intro) {
      const introOutput = await executeStepFunction(
        command.intro,
        null,
        initialData,
        null,
        callbacks
      );
      if (introOutput) {
        output += introOutput;
        output += "\n";
      }
    }

    // Add onComplete output if present
    if (command.onComplete) {
      const commandOutput = await executeStepFunction(
        command.onComplete,
        null,
        initialData,
        null,
        callbacks
      );
      if (commandOutput) {
        output += commandOutput;
      }
    }

    // Add outro if present
    if (command.outro) {
      if (output) output += "\n";
      const outroOutput = await executeStepFunction(
        command.outro,
        null,
        initialData,
        null,
        callbacks
      );
      if (outroOutput) {
        output += outroOutput;
      }
    }

    // Check for redirect (same as step-based commands)
    const redirectResult = await this.handleRedirect(
      null,
      command,
      null,
      initialData,
      callbacks
    );
    if (redirectResult) {
      return {
        output,
        nextCommand: redirectResult.command,
        nextCommandData: redirectResult.payload || {},
      };
    }

    return { output };
  }

  async validateInput(
    step,
    input,
    sessionData = {},
    loadedData = null,
    callbacks = {}
  ) {
    // If step is undefined (e.g., after command completion), validation passes
    if (!step) return true;
    if (!step.validate) return true;

    return await executeStepFunction(
      step.validate,
      input,
      sessionData,
      loadedData,
      callbacks
    );
  }

  async processStepOutro(
    step,
    input,
    sessionData,
    loadedData = null,
    callbacks = {}
  ) {
    if (!step.outro) return "";

    return await executeStepFunction(
      step.outro,
      input,
      sessionData,
      loadedData,
      callbacks
    );
  }

  async processCommandCompletion(command, sessionData, callbacks = {}) {
    let output = "";

    console.log("🎬 Processing command completion:", {
      commandName: command.name,
      hasOnComplete: !!command.onComplete,
      hasOutro: !!command.outro,
    });

    if (command.onComplete) {
      const commandOutput = await executeStepFunction(
        command.onComplete,
        null, // no input for onComplete
        sessionData,
        null, // no loadedData
        callbacks
      );
      console.log("✅ onComplete output:", commandOutput);
      output += commandOutput;
    }

    if (command.outro) {
      if (output) output += "\n";

      const outroOutput = await executeStepFunction(
        command.outro,
        null, // no input for outro
        sessionData,
        null, // no loadedData
        callbacks
      );
      console.log("👋 outro output:", outroOutput);
      if (outroOutput) {
        output += outroOutput;
      }
    }

    console.log("📦 Total completion output:", output);
    return output;
  }

  async handleRedirect(step, command, input, sessionData, callbacks = {}) {
    // Check for redirect function - prioritize step.redirect, fallback to command.redirect
    const redirectFunction = step?.redirect || command?.redirect;

    if (!redirectFunction) {
      return null;
    }

    try {
      // Support both sync and async redirect functions using executeStepFunction
      const redirectResult = await executeStepFunction(
        redirectFunction,
        input,
        sessionData,
        null,
        callbacks
      );

      console.log("🎯 Redirect function returned:", redirectResult);
      console.log("Redirect result type:", typeof redirectResult);

      // Handle both string returns and object returns with payload
      if (typeof redirectResult === "string") {
        console.log(`🔍 Checking if command "${redirectResult}" exists...`);
        const commandExists = !!this.commands[redirectResult];
        if (!commandExists) {
          console.warn(
            `Redirect target "${redirectResult}" not found. Available commands:`,
            Object.keys(this.commands)
          );
        }
        return commandExists ? { command: redirectResult } : null;
      } else if (redirectResult && typeof redirectResult === "object") {
        const { command, payload } = redirectResult;
        console.log(`🔍 Checking if object command "${command}" exists...`);
        const commandExists = command && !!this.commands[command];
        if (!commandExists && command) {
          console.warn(
            `Redirect target "${command}" not found. Available commands:`,
            Object.keys(this.commands)
          );
        }
        return commandExists ? { command, payload } : null;
      }

      if (redirectResult !== null && redirectResult !== undefined) {
        console.warn(
          `Redirect function returned unexpected type:`,
          typeof redirectResult,
          redirectResult
        );
      }

      return null;
    } catch (error) {
      console.error("Error in redirect function:", error);
      return null;
    }
  }
}
